/* eslint-disable import/no-extraneous-dependencies */
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import { Fragment, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { CommandProps } from '@tiptap/core';

interface SplitParagraphPart {
  node: ProseMirrorNode;
  selected: boolean;
}

interface Replacement {
  from: number;
  to: number;
  parts: SplitParagraphPart[];
}

const hasHardBreak = (node: ProseMirrorNode) => {
  let found = false;

  node.descendants((child) => {
    if (child.type.name === 'hardBreak') {
      found = true;
      return false;
    }

    return !found;
  });

  return found;
};

const isRangeSelected = (selectionFrom: number, selectionTo: number, from: number, to: number) => {
  if (from === to) {
    return selectionFrom <= from && selectionTo >= to;
  }

  return selectionFrom < to && selectionTo > from;
};

const splitParagraphAtHardBreaks = (
  node: ProseMirrorNode,
  pos: number,
  selectionFrom: number,
  selectionTo: number,
  selectAll: boolean,
) => {
  const parts: Array<{ content: ProseMirrorNode[]; from: number; to: number }> = [
    {
      content: [],
      from: pos + 1,
      to: pos + 1,
    },
  ];

  node.forEach((child, offset) => {
    const childFrom = pos + 1 + offset;

    if (child.type.name === 'hardBreak') {
      parts[parts.length - 1].to = childFrom;
      parts.push({
        content: [],
        from: childFrom + child.nodeSize,
        to: childFrom + child.nodeSize,
      });
      return;
    }

    parts[parts.length - 1].content.push(child);
    parts[parts.length - 1].to = childFrom + child.nodeSize;
  });

  while (parts.length > 1 && parts[parts.length - 1].content.length === 0) {
    parts.pop();
  }

  return parts.map((part) => ({
    node: node.type.create(node.attrs, Fragment.fromArray(part.content)),
    selected: selectAll || isRangeSelected(selectionFrom, selectionTo, part.from, part.to),
  }));
};

const isInsideListItem = ({ tr }: CommandProps, pos: number) => {
  const $pos = tr.doc.resolve(pos);
  const { depth: startDepth } = $pos;

  for (let depth = startDepth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === 'listItem') {
      return true;
    }
  }

  return false;
};

const splitSelectedParagraphsAtHardBreaks = (props: CommandProps) => {
  const { tr } = props;
  const { selection } = tr;
  const replacements: Replacement[] = [];

  const addReplacement = (node: ProseMirrorNode, pos: number) => {
    if (node.type.name !== 'paragraph' || isInsideListItem(props, pos + 1) || !hasHardBreak(node)) {
      return;
    }

    replacements.push({
      from: pos,
      to: pos + node.nodeSize,
      parts: splitParagraphAtHardBreaks(node, pos, selection.from, selection.to, selection.empty),
    });
  };

  if (selection.empty && selection.$from.parent.type.name === 'paragraph') {
    addReplacement(selection.$from.parent, selection.$from.before());
  } else {
    tr.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      addReplacement(node, pos);
    });
  }

  let selectedFrom: number | undefined;
  let selectedTo: number | undefined;
  let positionOffset = 0;

  replacements.forEach((replacement) => {
    let partPos = replacement.from + positionOffset;

    replacement.parts.forEach((part) => {
      if (part.selected) {
        selectedFrom = selectedFrom === undefined ? partPos + 1 : Math.min(selectedFrom, partPos + 1);
        selectedTo =
          selectedTo === undefined
            ? partPos + part.node.nodeSize - 1
            : Math.max(selectedTo, partPos + part.node.nodeSize - 1);
      }

      partPos += part.node.nodeSize;
    });

    positionOffset +=
      replacement.parts.reduce((size, part) => size + part.node.nodeSize, 0) - (replacement.to - replacement.from);
  });

  replacements.reverse().forEach(({ from, to, parts }) => {
    const nodes = parts.map((part) => part.node);

    tr.replaceWith(from, to, nodes);
  });

  if (selectedFrom !== undefined && selectedTo !== undefined) {
    tr.setSelection(TextSelection.create(tr.doc, selectedFrom, selectedTo));
  }

  return true;
};

export const HardBreakAwareBulletList = BulletList.extend({
  addCommands() {
    return {
      toggleBulletList:
        () =>
        ({ chain, commands }) => {
          if (this.editor.isActive(this.name)) {
            return commands.toggleList(this.name, this.options.itemTypeName, this.options.keepMarks);
          }

          return chain()
            .command(splitSelectedParagraphsAtHardBreaks)
            .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks)
            .run();
        },
    };
  },
});

export const HardBreakAwareOrderedList = OrderedList.extend({
  addCommands() {
    return {
      toggleOrderedList:
        () =>
        ({ chain, commands }) => {
          if (this.editor.isActive(this.name)) {
            return commands.toggleList(this.name, this.options.itemTypeName, this.options.keepMarks);
          }

          return chain()
            .command(splitSelectedParagraphsAtHardBreaks)
            .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks)
            .run();
        },
    };
  },
});
